import React from "react";
import LegalPage, { Section } from "@/components/LegalPage";
import { business, fullAddress } from "@/lib/business";

export default function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      subtitle={`The rules for using ${business.name}. Read this before placing an order.`}
      testId="terms-page"
    >
      <p>
        These Terms of Service ("Terms") govern your access to and use of {business.name} (the "Site")
        operated by {business.legalName} ("we", "us", "our"). By using the Site or placing an order, you
        agree to be bound by these Terms.
      </p>

      <Section n="1" title="Eligibility & account">
        <p>
          You must be at least 18 years old (or the age of majority in your jurisdiction) to place an order.
          When you create an account you are responsible for keeping your password secure and for any
          activity under your account. Notify us immediately at{" "}
          <a href={`mailto:${business.supportEmail}`} className="text-primary underline">
            {business.supportEmail}
          </a>{" "}
          if you suspect unauthorized access.
        </p>
      </Section>

      <Section n="2" title="Products, pricing & availability">
        <p>
          We do our best to display product images, descriptions, prices and specifications accurately.
          Colors and finishes may vary slightly due to your device. We reserve the right to correct
          typographical errors, change prices, or cancel orders where a listing was clearly wrong.
        </p>
        <p>
          All prices are shown in US Dollars (USD) and exclude applicable taxes, duties and shipping unless
          expressly stated. Products are subject to availability; if an item is out of stock after your
          order is placed, we will notify you and issue a full refund for that item.
        </p>
      </Section>

      <Section n="3" title="Orders, payment & authorization">
        <p>
          Placing an order is an offer to buy. We accept your offer when we send an order confirmation
          email or ship the item, whichever is later. Payment is captured at checkout via our processors
          (Stripe and/or PayPal). Any coupon, credit or bundle discount is applied automatically.
        </p>
        <p>
          We may refuse or cancel any order at our sole discretion — including for suspected fraud,
          incorrect pricing, or violations of these Terms.
        </p>
      </Section>

      <Section n="4" title="Shipping">
        <p>
          Orders are processed in {business.processingBusinessDays} business days. Domestic shipping
          typically takes {business.domesticShippingBusinessDays} business days; international can take{" "}
          {business.internationalShippingBusinessDays} business days depending on carrier and destination.
          Free standard shipping is included on orders over ${business.freeShippingThreshold}.
        </p>
        <p>
          Risk of loss and title passes to you when the carrier accepts the package. If a package is
          lost or stolen after successful delivery, please file a claim with the carrier first.
        </p>
      </Section>

      <Section n="5" title="Warranty">
        <p>
          Every product ships with a limited {business.warrantyMonths}-month manufacturer warranty
          against defects in materials or workmanship under normal use. The warranty does not cover
          accidental damage, misuse, water damage (unless the product is rated), unauthorized repairs
          or normal wear-and-tear.
        </p>
      </Section>

      <Section n="6" title="Returns & refunds">
        <p>
          See our <a href="/refund-policy" className="text-primary underline">Refund Policy</a> for the
          full terms. In short: {business.returnWindowDays}-day window from delivery, original condition,
          buyer pays return shipping unless the item arrived defective.
        </p>
      </Section>

      <Section n="7" title="Prohibited use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>Use the Site for any unlawful purpose or in violation of any regulation</li>
          <li>Attempt to gain unauthorized access to any part of the Site or our systems</li>
          <li>Interfere with other users' use of the Site</li>
          <li>Scrape, mirror or reverse-engineer the Site</li>
          <li>Use bots, coupon-stacking exploits or fraudulent payment methods</li>
        </ul>
      </Section>

      <Section n="8" title="Intellectual property">
        <p>
          All content on the Site — logos, product photography, text, code — is the property of{" "}
          {business.legalName} or its licensors and is protected by copyright and trademark laws. You may
          not use, copy or distribute anything from the Site without our written permission, except for
          personal, non-commercial browsing.
        </p>
      </Section>

      <Section n="9" title="User-generated content (reviews, questions)">
        <p>
          When you submit a review, photo, question or answer, you grant {business.legalName} a
          worldwide, royalty-free, perpetual license to display, reproduce and modify that content on the
          Site and in our marketing. You represent that you own the rights to what you submit and that
          it does not defame, harass or infringe on anyone.
        </p>
      </Section>

      <Section n="10" title="Disclaimers & limitation of liability">
        <p>
          The Site and products are provided "AS IS" without warranties of any kind beyond what is
          explicitly stated in our warranty section or required by law. To the maximum extent permitted
          by law, {business.legalName}'s total liability arising out of or related to your use of the Site
          or any product is limited to the amount you paid for the specific order giving rise to the
          claim.
        </p>
        <p>
          We are not liable for indirect, incidental, consequential, or punitive damages, including loss
          of profits, data or goodwill, even if advised of the possibility.
        </p>
      </Section>

      <Section n="11" title="Indemnification">
        <p>
          You agree to defend and indemnify {business.legalName} and its officers, employees and agents
          against any claim, damage, liability, cost or expense (including reasonable legal fees) arising
          from your breach of these Terms or misuse of the Site.
        </p>
      </Section>

      <Section n="12" title="Governing law & disputes">
        <p>
          These Terms are governed by the laws of the State of {business.address.state},{" "}
          {business.address.country}, without regard to conflict-of-law rules. Any dispute must be brought
          in the state or federal courts located in {business.address.state}, and you consent to the
          personal jurisdiction of those courts. Where required, small claims court remains available.
        </p>
      </Section>

      <Section n="13" title="Changes to these Terms">
        <p>
          We may update these Terms from time to time. When we do, we will change the "Last updated" date
          at the top and, for material changes, notify you by email or an on-site banner. Continued use
          of the Site after the changes take effect means you accept the updated Terms.
        </p>
      </Section>

      <Section n="14" title="Contact">
        <p>
          Questions? Email <a className="text-primary underline" href={`mailto:${business.supportEmail}`}>{business.supportEmail}</a> or
          reach us at:
        </p>
        <p className="text-muted-foreground">{business.legalName}<br />{fullAddress()}</p>
      </Section>

      <p className="text-xs text-muted-foreground/70 mt-8 pt-6 border-t border-white/5">
        Legal entity: {business.name} is operated as a sole proprietorship by {business.soleProprietor}, {business.address.city}, {business.address.state}, {business.address.country}.
      </p>
    </LegalPage>
  );
}
